import type { IClientSideStorageItem } from '../models/client-side-storage-item.model';
import { ClientSideStorageType } from '../models/client-side-storage-type.enum';

class CookieService {
  public static set(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};secure;path=/;samesite=lax`;
  }

  public static get(name: string): string | undefined {
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => cookie.substring(0, nameLenPlus) === `${name}=`)
        .map((cookie) => decodeURIComponent(cookie.substring(nameLenPlus)))[0] || undefined
    );
  }

  public static remove(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;secure;path=/;samesite=lax`;
  }
}

export class ClientSideStorageService {
  public static isConsentGiven(consentStatus: ClientSideStorageType): boolean {
    const currentStatus = this.getConsentStatus();

    return (currentStatus & consentStatus) === consentStatus;
  }

  public static isConsentStatusSet(): boolean {
    const consentStatus = CookieService.get('vt-consent-cookie');
    return !!consentStatus;
  }

  public static approveAll(): boolean {
    return this.setConsentStatus(ClientSideStorageType.Necessary | ClientSideStorageType.Functional | ClientSideStorageType.Analytics);
  }

  public static approveNecessary(): boolean {
    return this.setConsentStatus(ClientSideStorageType.Necessary);
  }

  public static setConsentStatus(consentStatus: ClientSideStorageType): boolean {
    CookieService.set('vt-consent-cookie', consentStatus.toString(), 365);
    if (this.changePromiseResolve) {
      this.changePromiseResolve(consentStatus);
    }
    this.handleMatomoConsent();

    return this.isConsentStatusSet() && this.getConsentStatus() === consentStatus;
  }

  private static getConsentStatus(): ClientSideStorageType {
    const consentStatus = CookieService.get('vt-consent-cookie');
    if (consentStatus) {
      const consentStatusNumber = Number(consentStatus);
      return consentStatusNumber < 1 ? ClientSideStorageType.Necessary : consentStatusNumber;
    }
    return ClientSideStorageType.Necessary;
  }

  private static changePromise: Promise<ClientSideStorageType> | undefined;

  private static changePromiseResolve: (value: ClientSideStorageType) => void | undefined;

  public static onConsentStatusChange(): Promise<ClientSideStorageType> {
    if (!this.changePromise) {
      this.changePromise = new Promise<ClientSideStorageType>((resolve) => {
        this.changePromiseResolve = resolve;
      });
    }
    return this.changePromise;
  }

  public static getCookie(item: IClientSideStorageItem): string | undefined {
    if (!item || !this.isConsentGiven(item.consentStatus)) {
      return undefined;
    }

    return CookieService.get(item.key);
  }

  public static setCookie(item: IClientSideStorageItem, value: string, days: number): void {
    if (!item || !this.isConsentGiven(item.consentStatus)) {
      return;
    }
    CookieService.set(item.key, value, days);
  }

  public static getLocalStorage(item: IClientSideStorageItem): string | undefined {
    try {
      if (!window.localStorage || !item || !this.isConsentGiven(item.consentStatus)) {
        return undefined;
      }
      return window.localStorage.getItem(item.key) || undefined;
    } catch {
      // Intentionally returning undefined to handle cases where localStorage is unavailable or access is restricted.
      return undefined;
    }
  }

  public static setLocalStorage(item: IClientSideStorageItem, value: string): void {
    try {
      if (!window.localStorage || !item || !this.isConsentGiven(item.consentStatus)) {
        return;
      }
      window.localStorage.setItem(item.key, value);
    } catch {
      /* empty */
    }
  }

  public static clearLocalStorage(item: IClientSideStorageItem): void {
    try {
      if (window.localStorage && item) {
        window.localStorage.removeItem(item.key);
      }
    } catch {
      /* empty */
    }
  }

  public static getSessionStorage(item: IClientSideStorageItem): string | undefined {
    try {
      if (!window.sessionStorage || !item || !this.isConsentGiven(item.consentStatus)) {
        return undefined;
      }
      return window.sessionStorage.getItem(item.key) || undefined;
    } catch {
      // Returning undefined as the default value when an error occurs.
      return undefined;
    }
  }

  public static setSessionStorage(item: IClientSideStorageItem, value: string): void {
    try {
      if (!window.sessionStorage || !item || !this.isConsentGiven(item.consentStatus)) {
        return;
      }
      window.sessionStorage.setItem(item.key, value);
    } catch {
      /* empty */
    }
  }

  public static clearSessionStorage(item: IClientSideStorageItem): void {
    try {
      if (window.sessionStorage && item) {
        window.sessionStorage.removeItem(item.key);
      }
    } catch {
      // Suppressing errors intentionally to ensure the application continues to function
      // even if sessionStorage operations fail (e.g., due to browser restrictions).
    }
  }

  public static handleMatomoConsent(): void {
    if (typeof window !== 'undefined' && window._paq) {
      if (this.isConsentGiven(ClientSideStorageType.Analytics)) {
        window._paq.push(['rememberConsentGiven']);
      } else {
        window._paq.push(['forgetConsentGiven']);
      }
    }
  }
}

export class ClientSideStorageItems {
  public static LocalStorage = {
    AutocompleteHistoryFrom: {
      key: 'vt-autocomplete-history-from-v3',
      consentStatus: ClientSideStorageType.Functional,
    },
    AutocompleteHistoryTo: {
      key: 'vt-autocomplete-history-to-v3',
      consentStatus: ClientSideStorageType.Functional,
    },
    AutocompleteHistoryDelayCompensationFrom: {
      key: 'vt-autocomplete-history-delay-compensation-from-v2',
      consentStatus: ClientSideStorageType.Functional,
    },
    AutocompleteHistoryDelayCompensationTo: {
      key: 'vt-autocomplete-history-delay-compensation-to-v2',
      consentStatus: ClientSideStorageType.Functional,
    },
    MunicipalityAutocompleteHistory: {
      key: 'vt-municipality-autocomplete-history',
      consentStatus: ClientSideStorageType.Functional,
    },
    LineAutocompleteHistory: {
      key: 'vt-line-autocomplete-history',
      consentStatus: ClientSideStorageType.Functional,
    },
    TrafficInformationOverviewMunicipality: {
      key: 'vt-traffic-information-overview-municipality',
      consentStatus: ClientSideStorageType.Functional,
    },
    TrafficInformationFeedbackUserId: {
      key: 'vt-traffic-information-feedback-user-id',
      consentStatus: ClientSideStorageType.Necessary,
    },
    TravelPlannerFiltersSave: {
      key: 'vt-travel-planner-filters-save',
      consentStatus: ClientSideStorageType.Functional,
    },
    TravelPlannerFilters: {
      key: 'vt-travel-planner-filters',
      consentStatus: ClientSideStorageType.Functional,
    },
    MicroMobilityFilters: {
      key: 'vt-micromobility-filters',
      consentStatus: ClientSideStorageType.Functional,
    },
    MultiFacetAutocompleteHistory: {
      key: 'vt-multi-facet-autocomplete-history',
      consentStatus: ClientSideStorageType.Functional,
    },
  };

  public static SessionStorage = {
    HideInformationBanners: {
      key: 'vt-hide-information-banners',
      consentStatus: ClientSideStorageType.Functional,
    },
  };

  public static Cookie = {
    SelectedMunicipality: {
      key: 'vt-selected-municipality',
      consentStatus: ClientSideStorageType.Necessary,
    },
  };
}

